import { useState, useEffect, useMemo, useContext } from 'react'
import { TextField } from '@mui/material'
import { fetchData } from '../../global/utils/fetch'
import { getMissionCapableData } from './api'
import { formatDateYyyyMmDd } from '../../global/utils/date/date-utils-yyyy-mm-dd'
import Dashboard, {
  DashboardColumn,
} from '../../global/components/dashboard/Dashboard'
import LocationDropDown from '../../global/components/location-drop-down/LocationDropDown'
import PrimaryButton from '../../global/components/buttons/primary-button/PrimaryButton'
import { ToastNotificationContext } from '../../global/context/toast-context/ToastNotificationContext'
import styles from './styles.module.css'
import {
  MissionCapableReportData,
  MissionCapableVanData,
} from '../../../../app/types/mission-capable-report'

type FlatVanRow = MissionCapableVanData & {
  locationName: string
  locationObjectId: string
}

function getDefaultFromDate(): Date {
  const date = new Date()
  date.setDate(date.getDate() - 30)
  return date
}

function MissionCapableReport() {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [fromDate, setFromDate] = useState<Date>(getDefaultFromDate())
  const [toDate, setToDate] = useState<Date>(new Date())
  const [locationFilter, setLocationFilter] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [reportData, setReportData] =
    useState<MissionCapableReportData | null>(null)

  async function fetchReport(signal?: AbortSignal) {
    setIsLoading(true)
    try {
      const data = await fetchData<MissionCapableReportData>(
        getMissionCapableData(
          fromDate,
          toDate,
          locationFilter || undefined,
        ),
        signal,
      )
      setReportData(data)
    } catch (err: any) {
      if (err?.name === 'AbortError') return
      console.error(err)
      setReportData(null)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Failed to load mission capable report',
        severity: 'error',
      })
    }
    if (!signal?.aborted) {
      setIsLoading(false)
    }
  }

  async function handleDownloadCsv() {
    try {
      const data = await fetchData<{ csv: string }>(
        getMissionCapableData(
          fromDate,
          toDate,
          locationFilter || undefined,
          true,
        ),
      )
      const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `mission-capable-report-${formatDateYyyyMmDd(fromDate)}-to-${formatDateYyyyMmDd(toDate)}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error(err)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Failed to download CSV',
        severity: 'error',
      })
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    fetchReport(controller.signal)
    return () => controller.abort()
  }, [fromDate, toDate, locationFilter])

  // Flatten van data for the table: one row per van, grouped by location
  const flatRows: FlatVanRow[] = useMemo(() => {
    if (!reportData) return []
    const rows: FlatVanRow[] = []
    for (const location of reportData.locations) {
      for (const van of location.vans) {
        rows.push({
          ...van,
          locationName: location.locationName,
          locationObjectId: location.locationObjectId,
        })
      }
    }
    return rows
  }, [reportData])

  const columns: DashboardColumn<FlatVanRow>[] = useMemo(
    () => [
      {
        Header: 'Location',
        accessor: 'locationName',
      },
      {
        Header: 'Van',
        accessor: 'vanName',
      },
      {
        Header: 'Down Hours',
        accessor: 'downHours',
        align: 'right' as const,
        Cell: ({ value }) => ({
          value: Number(value).toFixed(1),
        }),
      },
      {
        Header: 'Available Hours',
        accessor: 'availableHours',
        align: 'right' as const,
        Cell: ({ value }) => ({
          value: Number(value).toFixed(1),
        }),
      },
      {
        Header: 'Mission Capable %',
        accessor: 'missionCapablePercent',
        align: 'right' as const,
        Cell: ({ value }) => {
          const pct = Number(value)
          return {
            value: `${pct.toFixed(1)}%`,
            color: pct < 90 ? 'var(--status-error-text)' : undefined,
            bold: pct < 90,
          }
        },
      },
    ],
    [],
  )

  const totals = useMemo(() => {
    if (!reportData) return undefined
    const t = reportData.totals
    return [
      `${t.totalVans} vans`,
      t.totalDownHours.toFixed(1),
      t.totalAvailableHours.toFixed(1),
      `${t.missionCapablePercent.toFixed(1)}%`,
    ]
  }, [reportData])

  return (
    <div>
      <div className={styles.headerContainer}>
        <div className={styles.filtersContainer}>
          <LocationDropDown
            onLocationChange={(val: string) => setLocationFilter(val)}
          />
          <TextField
            label='From Date'
            type='date'
            value={formatDateYyyyMmDd(fromDate)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
            onChange={(e) => setFromDate(new Date(e.target.value))}
            disabled={isLoading}
          />
          <TextField
            label='To Date'
            type='date'
            value={formatDateYyyyMmDd(toDate)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
            onChange={(e) => setToDate(new Date(e.target.value))}
            disabled={isLoading}
          />
        </div>
        <PrimaryButton
          buttonName='Download CSV'
          onClick={handleDownloadCsv}
          style={{ width: 'max-content', height: 'max-content' }}
        />
      </div>

      <Dashboard<FlatVanRow>
        columns={columns}
        data={flatRows}
        isLoading={isLoading}
        totals={totals}
        totalsLabel='TOTAL'
        getRowKey={(row) => `${row.locationObjectId}-${row.vanObjectId}`}
        noDataMessage='No mission capable data available for the selected date range.'
      />
    </div>
  )
}

export default MissionCapableReport
